import { api } from "./client";
import type { ApiResponse } from "./types";

// ═══════════════════════════════════════════════════════════════════
// Admin endpoint clients (Phase 1E.1 Commit 3)
// All endpoints require `admin` or `super_admin` role -- the backend
// enforces this via the `requireAdmin` middleware. The Lab's
// useRole() hook decides whether to surface the corresponding UI.
// ═══════════════════════════════════════════════════════════════════

// ─── Verification Queue (unified moderation) ──────────────────────

export type VerifiableEntityType =
  | "institution"
  | "event_organizer"
  | "ref_interest_proposal";

export type VerificationRequestStatus =
  | "pending"
  | "under_review"
  | "approved"
  | "rejected"
  | "more_info_requested";

export interface VerificationRequest {
  id: string;
  entityType: VerifiableEntityType;
  entityId: string;
  status: VerificationRequestStatus;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNotes: string | null;
  rejectReason: string | null;
  documents: unknown;
  reviewer?: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
  } | null;
}

export function listVerificationQueue(params?: {
  entityType?: VerifiableEntityType;
  status?: VerificationRequestStatus;
  page?: number;
  limit?: number;
  order?: "asc" | "desc";
}) {
  return api.get<{
    success: true;
    data: {
      items: VerificationRequest[];
      meta: { page: number; limit: number; total: number; totalPages: number };
    };
  }>(
    "/admin/verification-queue",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getVerificationRequest(id: string) {
  return api.get<
    ApiResponse<{ request: VerificationRequest; entity: unknown }>
  >(`/admin/verification-queue/${id}`);
}

export function approveVerificationRequest(id: string, notes?: string) {
  return api.post<ApiResponse<VerificationRequest>>(
    `/admin/verification-queue/${id}/approve`,
    { notes },
  );
}

export function rejectVerificationRequest(
  id: string,
  body: { reason: string; notes?: string },
) {
  return api.post<ApiResponse<VerificationRequest>>(
    `/admin/verification-queue/${id}/reject`,
    body,
  );
}

export function requestVerificationInfo(id: string, notes: string) {
  return api.post<ApiResponse<VerificationRequest>>(
    `/admin/verification-queue/${id}/request-info`,
    { notes },
  );
}

// ─── Admin Interests Queue ────────────────────────────────────────

export type RefInterestStatus = "approved" | "pending" | "rejected";

export interface InterestProposal {
  id: string;
  code: string;
  canonicalNameEn: string;
  emoji: string | null;
  interestType: string;
  category: string;
  status: RefInterestStatus;
  submittedAt: string | null;
  submittedByUserId: string | null;
  rejectReason: string | null;
  proposalCount?: number;
}

export type InterestRejectReason =
  | "inappropriate"
  | "duplicate_existing"
  | "too_vague"
  | "out_of_scope"
  | "other";

// The queue endpoint only accepts limit/offset and always returns PENDING
// proposals (status is server-fixed); response is { items, total } — no meta.
export function listInterestsQueue(params?: {
  limit?: number;
  offset?: number;
}) {
  return api.get<
    ApiResponse<{ items: InterestProposal[]; total: number }>
  >(
    "/admin/interests/queue",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function approveInterestProposal(id: string, notes?: string) {
  return api.post<ApiResponse<InterestProposal>>(
    `/admin/interests/${id}/approve`,
    { notes },
  );
}

// Reject schema is { reason, details? }; `details` is REQUIRED when reason is
// "other" (server .refine).
export function rejectInterestProposal(
  id: string,
  body: { reason: InterestRejectReason; details?: string },
) {
  return api.post<ApiResponse<InterestProposal>>(
    `/admin/interests/${id}/reject`,
    body,
  );
}

// ─── Admin Analytics (Phase 1D.1) ─────────────────────────────────

export interface NotificationTypeStats {
  type: string;
  sent: number;
  delivered: number;
  opened: number;
  tapped: number;
  deliveryRate: number;
  openRate: number;
  tapRate: number;
  avgTimeToOpenMs: number | null;
}

export interface NotificationAnalytics {
  window: { startDate: string; endDate: string };
  totals: {
    sent: number;
    delivered: number;
    opened: number;
    tapped: number;
    deliveryRate: number;
    openRate: number;
    tapRate: number;
  };
  byType: NotificationTypeStats[];
}

export function getNotificationAnalytics(params?: {
  startDate?: string;
  endDate?: string;
  type?: string;
}) {
  return api.get<ApiResponse<NotificationAnalytics>>(
    "/admin/analytics/notifications",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export type FunnelName =
  | "identity_board"
  | "event_attendance"
  | "institution_lifecycle"
  | "event_organizer_lifecycle";

export interface FunnelStepBreakdown {
  step: string;
  uniqueUsers: number;
  totalEvents: number;
  conversionFromFirstStepPct: number;
  conversionFromPrevStepPct: number;
}

export interface FunnelReport {
  funnelName: FunnelName;
  steps: FunnelStepBreakdown[];
  window: { startDate: string; endDate: string };
}

export interface FunnelAnalytics {
  window: { startDate: string; endDate: string };
  funnels: FunnelReport[];
}

export function getFunnelAnalytics(params?: {
  funnelName?: FunnelName;
  startDate?: string;
  endDate?: string;
}) {
  return api.get<ApiResponse<FunnelAnalytics>>(
    "/admin/analytics/funnels",
    params as Record<string, string | number | boolean | undefined>,
  );
}

// ─── Yash Fraud Queue ─────────────────────────────────────────────

export interface YashFraudFlag {
  id: string;
  userId: string;
  actionType: string;
  yash: number;
  fraudReason: string | null;
  status: "pending" | "fraud_held" | "cleared" | "reversed";
  createdAt: string;
  user?: { id: string; name: string; email: string };
}

// The fraud queue only surfaces pending / fraud_held flags.
export function getYashFraudQueue(params?: {
  status?: "pending" | "fraud_held";
  page?: number;
  limit?: number;
}) {
  return api.get<
    ApiResponse<{ items: YashFraudFlag[]; meta: { total: number } }>
  >(
    "/yash/admin/fraud-queue",
    params as Record<string, string | number | boolean | undefined>,
  );
}

// processFraudSchema accepts { action, reason? } — the moderator note maps to
// `reason`, and the only valid actions are clear / confirm_fraud.
export function processYashFraud(
  id: string,
  action: "clear" | "confirm_fraud",
  reason?: string,
) {
  return api.patch<ApiResponse<YashFraudFlag>>(
    `/yash/admin/fraud-queue/${id}`,
    { action, reason },
  );
}

// ─── Dharohar Moderation Queue ────────────────────────────────────

export type DharoharContentSourceStatus =
  | "fetched"
  | "pending_review"
  | "approved"
  | "rejected"
  | "error";

// Each queue row is a content-source record; the parsed entity fields (title,
// state, etc.) live under `contentDetails`, not on the row itself.
export interface DharoharSubmission {
  id: string;
  contentType: "personality" | "art_form" | "handicraft";
  contentId: string | null;
  sourceUrl: string | null;
  sourceName: string | null;
  status: DharoharContentSourceStatus;
  fetchedAt: string | null;
  errorMessage: string | null;
  contentDetails: {
    name?: string;
    title?: string;
    stateCode?: string | null;
    [key: string]: unknown;
  } | null;
  createdAt: string;
}

export function getDharoharModerationQueue(params?: {
  status?: DharoharContentSourceStatus;
  page?: number;
  limit?: number;
}) {
  return api.get<
    ApiResponse<{ sources: DharoharSubmission[]; meta: { total: number } }>
  >(
    "/dharohar/admin/moderation-queue",
    params as Record<string, string | number | boolean | undefined>,
  );
}

// moderationActionSchema is { action, reason? } — note maps to `reason`.
export function processDharoharModeration(
  id: string,
  body: { action: "approve" | "reject"; reason?: string },
) {
  return api.patch<ApiResponse<DharoharSubmission>>(
    `/dharohar/admin/moderation-queue/${id}`,
    body,
  );
}

// ─── Dharohar Record Review ───────────────────────────────────────
// A moderator reads a record once, with all of its sources, and decides the
// whole record. The per-source queue above stays for records in a mixed state.

export type DharoharReviewContentType =
  | "personality"
  | "art_form"
  | "handicraft"
  | "significance";

export type DharoharReviewStatus =
  | "pending_review"
  | "approved"
  | "rejected"
  | "unsourced";

export interface DharoharReviewRecordSummary {
  contentType: DharoharReviewContentType;
  id: string;
  contentKey: string | null;
  name: string;
  nameLocal: string | null;
  stateCode: string | null;
  reviewStatus: string;
  sourceCount: number;
  moderatedAt: string | null;
  updatedAt: string;
}

export interface DharoharReviewCounts {
  pendingReview: number;
  approved: number;
  rejected: number;
  unsourced: number;
}

export interface DharoharReviewSource {
  id: string;
  sourceUrl: string;
  sourceName: string;
  tier: string | null;
  claimKeys: string[];
  retrievedAt: string;
  status: string;
  errorMessage: string | null;
  corpusId: string | null;
  revision: number | null;
}

export interface DharoharReviewRecord {
  contentType: DharoharReviewContentType;
  id: string;
  contentKey: string | null;
  contentHash: string | null;
  reviewStatus: string;
  allowedActions: string[];
  isVerified: boolean;
  isActive: boolean;
  moderatedBy: string | null;
  moderatedAt: string | null;
  record: Record<string, unknown>;
  sources: DharoharReviewSource[];
}

export interface DharoharReviewDecision {
  contentType: DharoharReviewContentType;
  id: string;
  action: "approve" | "reject";
  previousStatus: string;
  reviewStatus: string;
  sourcesDecided: number;
  moderatedAt: string;
}

export function getDharoharRecordsForReview(params: {
  contentType: DharoharReviewContentType;
  status?: DharoharReviewStatus;
  stateCode?: string;
  page?: number;
  limit?: number;
}) {
  return api.get<
    ApiResponse<{
      records: DharoharReviewRecordSummary[];
      counts: DharoharReviewCounts;
      meta: { page: number; limit: number; total: number; totalPages: number };
    }>
  >(
    "/dharohar/admin/records",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export function getDharoharRecordForReview(
  contentType: DharoharReviewContentType,
  id: string,
) {
  return api.get<ApiResponse<DharoharReviewRecord>>(
    `/dharohar/admin/records/${contentType}/${id}`,
  );
}

// Send back the contentHash from the read. The API refuses a decision on
// content that changed after that read.
export function decideDharoharRecord(
  contentType: DharoharReviewContentType,
  id: string,
  body: {
    action: "approve" | "reject";
    contentHash: string | null;
    reason?: string;
  },
) {
  return api.patch<ApiResponse<DharoharReviewDecision>>(
    `/dharohar/admin/records/${contentType}/${id}`,
    body,
  );
}

// ─── Provider verifications (separate from the D5 unified queue) ───
// GET  /admin/verifications          — pending provider verifications
// POST /admin/verifications/:id/review — approve / reject

export interface AdminProviderVerification {
  id: string;
  type: string;
  status: string;
  documents?: string[];
  notes?: string | null;
  createdAt?: string;
  provider?: { id: string; name: string; slug?: string };
  submitter?: { id: string; name: string };
  [key: string]: unknown;
}

export function listProviderVerifications(params?: { page?: number; limit?: number }) {
  return api.get<{
    success: true;
    data: AdminProviderVerification[];
    meta?: unknown;
  }>("/admin/verifications", params as Record<string, string | number | undefined>);
}

export function reviewProviderVerification(
  id: string,
  body: { status: "approved" | "rejected"; reviewNotes?: string },
) {
  return api.post<ApiResponse<unknown>>(`/admin/verifications/${id}/review`, body);
}

// ─── Recruitment (admin) ────────────────────────────────────────────
// Two surfaces because they answer two different questions. The worklist ranks
// LISTINGS and is structurally silent about a shelf that has none; the
// shelf-gap report is the only one that can speak about an empty trade.

export interface ShelfGap {
  categoryCode: string;
  name: string;
  shoppers: number;
  /** Of those, how many saw zero businesses. THE recruitment number. */
  foundNothing: number;
  businesses: number;
  /** Only a claimed listing can take a stall, so 0 means the shelf cannot participate. */
  claimed: number;
  stalls: number;
  /** Null when there is nothing true to say yet — not an empty string. */
  pitch: string | null;
}

export interface ShelfGaps {
  metro: string;
  windowDays: number;
  shelves: ShelfGap[];
  /** Shopping trips where no shelf could be named at all — a category we may be missing. */
  unshelvedShoppers: number;
  /**
   * Searches that came back empty, split by whether we know where the person
   * was. Most searches carry no location, so the two must not be added
   * together — recruitment is a decision about which town to drive to.
   */
  searchesWithNothing: { inThisMetro: number; locationNotGiven: number };
  /**
   * Words searched for that appear in NO content. Each is a trade or product
   * nobody offers. Platform-wide, not metro-scoped.
   */
  wordsNobodyHasWritten: { term: string; count: number }[];
}

export interface OutreachRow {
  providerId: string;
  name: string;
  slug: string;
  categoryCode: string;
  city: string;
  state: string;
  vouchCount: number;
  /** Unanswered community questions in this trade + metro. */
  waitingAsks: number;
  /** Shoppers shown NOTHING in this trade + metro, last 30 days. */
  waitingShoppers: number;
  contact: { email: string | null; phone: string | null; website: string | null };
  outreachStatus: string;
  outreachNotes: string | null;
  lastContactedAt: string | null;
  score: number;
}

export function getShelfGaps(params?: { metro?: string; days?: number }) {
  const q = new URLSearchParams();
  if (params?.metro) q.set("metro", params.metro);
  if (params?.days) q.set("days", String(params.days));
  const qs = q.toString();
  return api.get<ApiResponse<ShelfGaps>>(`/admin/providers/shelf-gaps${qs ? `?${qs}` : ""}`);
}

export function getOutreachWorklist(params?: { city?: string; category?: string; limit?: number }) {
  const q = new URLSearchParams();
  if (params?.city) q.set("city", params.city);
  if (params?.category) q.set("category", params.category);
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  return api.get<
    ApiResponse<{ rows: OutreachRow[]; totals: { unclaimed: number; contactable: number } }>
  >(`/admin/providers/outreach${qs ? `?${qs}` : ""}`);
}
